import React, { useCallback, useMemo } from 'react';

import { useTranslation } from '@lyearn/i18n';
import { ArrowIosDownward } from '@lyearn/icons';
import { cn } from '@lyearn/style';
import { Stack, Typography } from '@lyearn/ui';
import SingleSelectFilter from '@/components/FilterManager/SingleSelectFilter';
import { SortMenuProps } from '@/components/SortMenu/types';
import { FilterOperatorEnum, FilterPropertyTypeEnum } from '@/types/schema';

import { operatorStyles } from './styles';
import { getSelectedOperator, Operator, OperatorType } from './utils';

interface PropertiesOperatorProps {
  operators: Operator[];
  propertyType?: FilterPropertyTypeEnum;
  selectedOperator?: OperatorType;
  onOperatorChange: (operator: FilterOperatorEnum) => void;
  size?: 'small' | 'medium';
  disabled?: boolean;
  overrides?: SortMenuProps['overrides'];
  className?: string;
}

const OVERRIDES: SortMenuProps['overrides'] = {
  PopoverContent: {
    maxWidth: 17,
  },
};

const HEIGHT = {
  small: 'h-40',
  medium: 'h-48',
};

function PropertiesOperator(props: PropertiesOperatorProps) {
  const { t } = useTranslation('Group');
  const {
    propertyType,
    selectedOperator,
    onOperatorChange,
    operators,
    size = 'small',
    disabled,
    className,
  } = props;
  const selectedItem = useMemo(() => {
    return getSelectedOperator(operators, selectedOperator);
  }, [selectedOperator, operators]);
  const selectedValue = selectedItem?.id ? [selectedItem?.id] : undefined;
  const _onOperatorChange = useCallback(
    (val: string) => onOperatorChange(val as FilterOperatorEnum),
    [onOperatorChange],
  );

  return (
    <SingleSelectFilter
      disabled={disabled ?? !propertyType}
      items={operators}
      overrides={props.overrides ?? OVERRIDES}
      selectedItems={selectedValue}
      onChange={_onOperatorChange}>
      <Stack
        alignItems="center"
        className={cn(
          className ?? operatorStyles,
          'flex-shrink-0 cursor-pointer rounded-8 border border-solid border-interactive-white-hover pe-12 ps-16 hover:border-border-dark',
          HEIGHT[size],
          { 'bg-bg-secondary': disabled },
        )}
        justifyContent="between">
        <Typography
          className="truncate"
          color={!selectedItem?.label || disabled ? 'text-text-placeholder' : 'text-text-primary'}
          variant={size === 'small' ? 'body-short-02' : 'body-short-01'}>
          {selectedItem?.label ? selectedItem.label : t('Group:Editor.Operators')}
        </Typography>
        <ArrowIosDownward className="h-20 w-20 ms-12" fillColorToken="icon-placeholder" />
      </Stack>
    </SingleSelectFilter>
  );
}

export default PropertiesOperator;
